/**
 * Done. / ChiroCPD — NZCB Board Portal Field Mappings
 *
 * 8 fields that practitioners must copy-paste into the Board portal.
 * There is NO Board API — copy-paste is the only solution.
 *
 * field_order is SACRED — these must match the Board portal exactly.
 * DO NOT reorder without validation against a current Board portal screenshot.
 *
 * Version: 2024.1 (based on NZCB CPD Policy January 2022, current 2024)
 * Labels: STUB — await Jo's portal screenshots for confirmed label text.
 *
 * Reference: worker/src/routes/export.ts field_order mapping.
 */

import type { PortalFieldMapping } from '@/lib/types';

export const PORTAL_FIELD_VERSION = '2024.1' as const;

/**
 * 8 NZCB Board portal fields in copy-paste order.
 * Array index 0–7 maps to fieldOrder 1–8.
 */
export const PORTAL_FIELD_MAPPINGS: PortalFieldMapping[] = [
  {
    fieldName: 'rationale',
    fieldLabel: 'Rationale / Learning Need',
    fieldOrder: 1,
    fieldType: 'textarea',
    description: 'Why you chose this CPD activity and what learning need it addresses.',
    stubContent: 'To advance clinical assessment skills through structured peer review and evidence-based practice development, aligned with Competency Principle 2: Chiropractic Expertise.',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'objectives',
    fieldLabel: 'Learning Objectives',
    fieldOrder: 2,
    fieldType: 'textarea',
    description: 'Specific objectives you aimed to achieve from this activity.',
    stubContent: '1. Improve differential diagnosis accuracy\n2. Apply evidence-based protocols in clinical practice\n3. Enhance patient communication techniques',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'activity_type',
    fieldLabel: 'Activity Type',
    fieldOrder: 3,
    fieldType: 'select',
    description: 'Board-defined CPD activity category (one of 16 approved types).',
    stubContent: 'Conference Attendance',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'activity_description',
    fieldLabel: 'Activity Description',
    fieldOrder: 4,
    fieldType: 'textarea',
    description: 'What the activity involved — title, provider, content summary.',
    stubContent: 'NZ Chiropractic Conference 2026 — Annual national conference covering advanced clinical topics, research updates, and professional development workshops.',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'hours',
    fieldLabel: 'Hours Completed',
    fieldOrder: 5,
    fieldType: 'number',
    description: 'Verified hours of CPD completed for this activity.',
    stubContent: '8',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'date_completed',
    fieldLabel: 'Date Completed',
    fieldOrder: 6,
    fieldType: 'date',
    description: 'Date the CPD activity was completed (DD/MM/YYYY for portal).',
    stubContent: '15/03/2026',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'outcome',
    fieldLabel: 'Outcomes Achieved',
    fieldOrder: 7,
    fieldType: 'textarea',
    description: 'What you actually learned or achieved from the activity.',
    stubContent: 'Gained updated knowledge of spinal manipulation techniques validated by recent RCT evidence. Identified two protocols to trial in practice.',
    version: PORTAL_FIELD_VERSION,
  },
  {
    fieldName: 'review',
    fieldLabel: 'Reflective Statement',
    fieldOrder: 8,
    fieldType: 'textarea',
    description: 'Critical reflection on how this learning has or will change your practice.',
    stubContent: 'This activity challenged my existing approach to lumbar assessment. I will integrate the evidence-based screening tool introduced at the conference into my standard intake process over the next month.',
    version: PORTAL_FIELD_VERSION,
  },
] as const;

/**
 * Look up a field mapping by its fieldName.
 */
export function getPortalField(fieldName: string): PortalFieldMapping | undefined {
  return PORTAL_FIELD_MAPPINGS.find(f => f.fieldName === fieldName);
}

/**
 * Get guidance text for an empty field based on its type.
 */
export function getFieldGuidance(fieldName: string): string {
  const field = getPortalField(fieldName);
  if (!field) return 'Log activities to populate this field.';

  const guidance: Record<string, string> = {
    rationale: 'Complete the onboarding survey to generate your Annual Plan, which populates this field.',
    objectives: 'This field is completed directly in the Board portal.',
    activity_type: 'Log a CPD activity to populate this field.',
    activity_description: 'Log a CPD activity with a title and description.',
    hours: 'Log a CPD activity with hours completed.',
    date_completed: 'Log a CPD activity with a completion date.',
    outcome: 'Add an outcomes & reflections entry to this activity.',
    review: 'Add a reflective statement to this activity.',
  };

  return guidance[fieldName] ?? 'Log activities to populate this field.';
}
